import { useState, useRef, useEffect, JSX, MouseEventHandler } from 'react';
import { twMerge } from 'tailwind-merge';

import { Badge, Text } from '@videoblocks/design-system';
import { Play, Pause } from '@videoblocks/react-icons';

import { type Voice } from '../types';
import { formatDisplayText } from './utils';

const VoiceCard = ({
    voice,
    selectedVoice,
    setSelectedVoice,
    playingVoiceId,
    setPlayingVoiceId,
    isCompact = false,
}: {
    voice: Voice;
    selectedVoice: Voice['voice_id'] | null;
    setSelectedVoice: (voiceId: Voice['voice_id']) => void;
    playingVoiceId?: Voice['voice_id'] | null;
    setPlayingVoiceId?: (voiceId: Voice['voice_id']) => void;
    isCompact?: boolean;
}): JSX.Element => {
    const { descriptive, use_case, name, description, preview_url, voice_id } = voice;
    const formattedUseCase = formatDisplayText(use_case);

    const [isHovered, setIsHovered] = useState(false);
    const [hasImageError, setHasImageError] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const imageSrc = `/assets/voiceoverAvatars/${use_case}.svg`;

    useEffect(() => {
        if (preview_url) {
            audioRef.current = new Audio(preview_url);

            const audio = audioRef.current;

            const handlePlay = () => setPlayingVoiceId?.(voice_id);
            const handleEnded = () => {
                setIsPlaying(false);
            };
            audio.addEventListener('play', handlePlay);
            audio.addEventListener('ended', handleEnded);

            return () => {
                audio.removeEventListener('play', handlePlay);
                audio.removeEventListener('ended', handleEnded);
                audio.pause();
                audio.src = '';
            };
        }
    }, [preview_url, voice_id, setPlayingVoiceId]);

    useEffect(() => {
        if (playingVoiceId !== voice_id && audioRef.current) {
            audioRef.current.pause();
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setIsPlaying(false);
        }
    }, [playingVoiceId, voice_id]);

    const handlePlayPause: MouseEventHandler<HTMLButtonElement> = (event) => {
        event.stopPropagation();

        if (!preview_url) {
            return;
        }

        const audio = audioRef.current;

        if (isPlaying) {
            audio?.pause();
        } else {
            if (audio) {
                audio.currentTime = 0;
                void audio.play();
            }
        }
        setIsPlaying(!isPlaying);
    };

    const showPlayControls = isHovered || isPlaying;

    return (
        <div
            data-testid="voice-card"
            onClick={() => setSelectedVoice(voice_id)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={twMerge(
                'bg-gray-900 cursor-pointer transition-all duration-200 overflow-hidden flex gap-3 h-auto',
                !isCompact && 'p-4 rounded-lg',
                !isCompact && selectedVoice === voice_id ? 'ring-2 ring-white' : '',
            )}
        >
            <div className="flex gap-3 h-full">
                <div className="relative">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full overflow-hidden relative bg-gray-600">
                        {!hasImageError && (
                            <img
                                src={imageSrc}
                                alt="voice avatar"
                                width="48"
                                height="48"
                                className="w-full h-full object-cover"
                                onError={() => setHasImageError(true)}
                                loading="lazy"
                                key={`avatar-${use_case}`}
                            />
                        )}

                        {hasImageError && (
                            <div
                                data-testid="image-error-gradient"
                                className="w-full h-full bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600"
                            />
                        )}

                        <button
                            className={twMerge(
                                'absolute inset-0 flex items-center justify-center text-white transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed rounded-full',
                                showPlayControls && preview_url ? 'hover:bg-blue-600' : '',
                                showPlayControls ? 'opacity-100' : 'opacity-0',
                                isPlaying ? 'bg-blue-500' : 'bg-gray-400',
                            )}
                            onClick={handlePlayPause}
                            data-testid="play-pause-button"
                            disabled={!preview_url}
                            title={!preview_url ? 'No preview available' : isPlaying ? 'Pause' : 'Play'}
                        >
                            {isPlaying ? (
                                <Pause className="w-6 h-6 fill-current" />
                            ) : (
                                <Play className="w-6 h-6 fill-current ml-0.5" />
                            )}
                        </button>
                    </div>
                </div>

                <div className="flex-1 h-full">
                    <div className="flex items-center gap-2 mb-2">
                        <Badge
                            size="small"
                            text={formattedUseCase}
                            fontWeight="semibold"
                            className="bg-gray-800 text-white"
                        />
                        <Badge
                            size="small"
                            text={descriptive}
                            fontWeight="semibold"
                            className="bg-gray-800 text-white"
                        />
                    </div>
                    <Text className={twMerge(!isCompact && 'mb-2')}>{name}</Text>
                    {!isCompact && (
                        <Text fontSize="sm" fontWeight="regular" className="overflow-hidden text-ellipsis">
                            {description}
                        </Text>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VoiceCard;
